import clsx from 'clsx';
import { useRegisterBreadcrumbTab } from '../../context/BreadcrumbContext';

export interface TabItem<T extends string = string> {
  id: T;
  label: string;
}

interface TabBarProps<T extends string> {
  tabs: readonly TabItem<T>[];
  active: T;
  onSelect: (id: T) => void;
  size?: 'sm' | 'md';
  /**
   * The page-level tab bar names the active tab in the header breadcrumb. Pass
   * false for a nested (second-level) bar so it doesn't override its parent.
   */
  breadcrumb?: boolean;
}

/** The one tab style: underline, used for every page- and section-level tab row. */
export function TabBar<T extends string>({ tabs, active, onSelect, size = 'md', breadcrumb = true }: TabBarProps<T>) {
  const activeLabel = tabs.find((t) => t.id === active)?.label;
  useRegisterBreadcrumbTab(breadcrumb ? activeLabel : null);

  const padding = size === 'md' ? 'px-3 py-2 text-body-sm' : 'px-3 py-1.5 text-body-xs';
  return (
    <div className="flex gap-1 border-b border-border-light overflow-x-auto" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={active === tab.id}
          onClick={() => onSelect(tab.id)}
          className={clsx(
            padding,
            '-mb-px whitespace-nowrap font-medium border-b-2 transition-colors',
            active === tab.id
              ? 'border-text-primary text-text-primary'
              : 'border-transparent text-text-tertiary hover:text-text-secondary'
          )}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

interface SegmentedControlProps<T extends string> {
  options: readonly TabItem<T>[];
  value: T;
  onChange: (id: T) => void;
  ariaLabel?: string;
}

/**
 * The one small toggle for periods, granularity and view modes ("30 days / 90
 * days", "Weekly / Daily", "Board / List"). Replaces the five hand-rolled
 * period toggles.
 */
export function SegmentedControl<T extends string>({ options, value, onChange, ariaLabel }: SegmentedControlProps<T>) {
  return (
    <div
      className="inline-flex items-center gap-0.5 rounded border border-border-light bg-surface-secondary p-0.5"
      role="radiogroup"
      aria-label={ariaLabel}
    >
      {options.map((opt) => (
        <button
          key={opt.id}
          type="button"
          role="radio"
          aria-checked={value === opt.id}
          onClick={() => onChange(opt.id)}
          className={clsx(
            'rounded-sm px-3 py-1 text-body-xs font-medium whitespace-nowrap transition-colors',
            value === opt.id
              ? 'bg-surface-primary text-text-primary shadow-subtle'
              : 'text-text-tertiary hover:text-text-secondary'
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
